import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EventRow } from "@/components/activity/event-row"
import { RecentActivityEmptyState } from "@/components/activity/recent-activity-empty-state"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { creatorProfilePath } from "@/routes"
import type { CreatorRecentActivityProps } from "@/types/recent-activity"

/**
 * OKS-393 — Standalone Recent Activity view (`/profile/activity`).
 *
 * Shows the latest 10 events for the creator's active membership in the
 * spacious (`page`) row density, reachable via the "See all →" link on
 * `/profile`. Back affordance returns to the profile.
 *
 * # Brand isolation
 *
 * `events` are scoped server-side to `Current.membership` — no
 * client-side query, no cross-brand surface. Switching brand context
 * triggers a full Inertia visit (`CrewSwitcher`) which re-renders this
 * page for the new brand.
 */
export default function CreatorRecentActivity({
  events,
}: CreatorRecentActivityProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("recent_activity.page_title")} />

      <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-6">
        <header className="flex flex-col gap-3">
          <Link
            href={creatorProfilePath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
            data-testid="recent-activity-back"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t("recent_activity.back")}
          </Link>
          <h1 className="text-2xl font-bold">{t("recent_activity.title")}</h1>
        </header>

        {events.length === 0 ? (
          <Card className="border-0 shadow-none">
            <CardContent className="px-0 py-2">
              <RecentActivityEmptyState />
            </CardContent>
          </Card>
        ) : (
          <ul
            data-testid="recent-activity-list"
            className="border-border overflow-hidden rounded-lg border"
          >
            {events.map((event) => (
              <EventRow key={event.id} event={event} variant="page" />
            ))}
          </ul>
        )}
      </main>
    </AppLayout>
  )
}
